import { api } from "@/lib/api";

// After returning from Stripe the mobile backend does NOT rely on the webhook —
// the client must call POST /donations/{id}/confirm to finalize the donation.
// The backend builds Stripe's success_url from the request host (our proxy host),
// so we (a) pass our own success_url/cancel_url and (b) stash the donation id so we
// can confirm it whenever the user lands back on the web app.
const PENDING_KEY = "blessed_pending_confirm";
const MAX_AGE_MS = 10 * 60 * 1000;

export const stashPending = (donationId) => {
  if (!donationId) return;
  try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ id: donationId, ts: Date.now() })); } catch {}
};

export const readPending = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const { id, ts } = JSON.parse(raw);
    if (!id || Date.now() - ts > MAX_AGE_MS) { sessionStorage.removeItem(PENDING_KEY); return null; }
    return id;
  } catch { return null; }
};

export const clearPending = () => { try { sessionStorage.removeItem(PENDING_KEY); } catch {} };

// Poll the backend to finalize a donation. Mobile polls up to 8x @ 1.2s.
// Terminal statuses: "sent" / "fee_paid".
export const pollConfirm = async (donationId, { attempts = 8, spacingMs = 1200 } = {}) => {
  let last = null;
  for (let i = 0; i < attempts; i++) {
    try {
      const { data } = await api.post(`/donations/${donationId}/confirm`);
      last = data?.status || last;
      if (last === "sent" || last === "fee_paid") return last;
    } catch { /* keep polling — payment may still be settling */ }
    if (i < attempts - 1) await new Promise((r) => setTimeout(r, spacingMs));
  }
  return last;
};

// Start a Stripe checkout: attach return URLs, stash the donation id, return the checkout url.
export const startCheckout = async (body) => {
  const origin = window.location.origin;
  const payload = {
    ...body,
    success_url: `${origin}/thanks`,
    cancel_url: body.recipient_id ? `${origin}/donate/${body.recipient_id}` : origin,
  };
  const { data } = await api.post("/donations/create-checkout", payload);
  const donationId = data?.donation_id || data?.id || data?.donation?.id || null;
  stashPending(donationId);
  const url = data?.url || data?.checkout_url || data?.session_url;
  return { url, donationId, data };
};
